import { components, useApi } from "@appretio/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateCubeMutation } from "./cubeMutationInvalidation";
import { forecastBudgetVersionsQueryKeys } from "./queryKeys";

type CreateCubeInput =
  | components["schemas"]["CreateUserCubeInput"]
  | components["schemas"]["CreateBudgetCubeInput"];

/**
 * Mutation hook to create a new cube (user or budget). The ``mode``
 * field on the input selects the variant on the discriminated
 * ``/v2/forecast/cubes/create`` endpoint.
 *
 * Budget creations only invalidate the selected brand's budget-versions
 * list (keeping other brands' caches intact); user creations invalidate
 * the broader cube-versions surface so the new cube appears in every
 * dropdown where it might be selectable.
 */
export function useCreateCube() {
  const apiClient = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCubeInput) => {
      const response = await apiClient.POST("/v2/forecast/cubes/create", {
        body: input
      });
      if (!response.data?.result) {
        throw new Error("Missing result in create cube response");
      }
      return response.data.result;
    },
    onSuccess: async (_data, variables) => {
      if (variables.mode === "budget") {
        await queryClient.invalidateQueries({
          // No brand means the tenant-scoped budget — the key the fetch used.
          queryKey: forecastBudgetVersionsQueryKeys.data(
            variables.brand ?? null
          )
        });
      } else {
        await invalidateCubeMutation(queryClient, { versions: true });
      }
    }
    // Error toasts are owned by the calling modal (CreateUserCubeModal /
    // CreateBudgetCubeModal) — they show a more specific message. No
    // ``meta.errorMessage`` here to avoid a duplicate global toast.
  });
}
